import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import ButtonNewItem from "~/components/buttons/ButtonNewItem";
import CurrencyInput from "~/components/formElements/CurrencyInput";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormInputError from "~/components/formElements/FormInputError";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import PaymentTargetDaysInput from "~/components/formElements/PaymentTargetInput";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { DEFAULT_PAYMENT_TARGET_DAYS } from "~/modules/billing/constants/billingConstants.ts";
import { createQuota } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import type { CreateQuotaData } from "~/modules/client/api/quota/quotaTypes.ts";
import QuotaProjectPhaseSelector
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/components/QuotaProjectPhaseSelector.tsx";
import SelectedQuotaProjectPhase
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/components/SelectedQuotaProjectPhase.tsx";
import type { ProjectPhaseForQuotaAssignment } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import BillingInvoiceRecipientFormSection from "~/modules/project/components/forms/formSections/InvoiceRecipientFormSection";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = Omit<CreateQuotaData, "invoiceDeliveryEndDate" | "invoiceDeliveryStartDate" | "projectPhaseIds"> & {
	invoiceDeliveryStartDate: Date;
	invoiceDeliveryEndDate: Date;
	paymentTargetDaysCustom?: number | null;
}

type Props = {
	clientId: string;
	onCancel: () => void;
	onSuccess: () => void;
};

const CreateQuotaForm = ({
	clientId,
	onCancel,
	onSuccess,
}: Props) => {
	const [selectedPhasesData, setSelectedPhasesData] = useState<ProjectPhaseForQuotaAssignment[]>([]);
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [showPhaseSelect, setShowPhaseSelect] = useState<boolean>(false);

	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(clientId);
	const clientsInvoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(clientId);


	const schema = useMemo(() => {
		return yup.object({
			clientContactPersonId: yup.string().nullable().default(null),
			budgetCents: yup.number().required().min(1, "Das Kontingent muss größer als 0 € sein."),
			invoiceDeliveryStartDate: yup.date().required(),
			invoiceDeliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("invoiceDeliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			invoiceRecipientId: yup.string().nullable().default(null),
			orderNumber: yup.string().required("Bitte gib eine Auftragsnummer ein."),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
			title: yup.string().required("Bitte gib einen Titel für das Kontingent ein."),
		}).test("atLeastOneField", "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.", (value) =>
			Boolean(value.clientContactPersonId || value.invoiceRecipientId),
		);
	}, []);

	const defaultValues = useMemo(() => {
		return {
			clientContactPersonId: null,
			budgetCents: 0,
			invoiceDeliveryStartDate: undefined,
			invoiceDeliveryEndDate: undefined,
			invoiceRecipientId: null,
			orderNumber: "",
			paymentTargetDays: DEFAULT_PAYMENT_TARGET_DAYS,
			paymentTargetDaysCustom: null,
			projectPhaseIds: [],
			title: "",
		};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid, errors },
		watch,
		setValue,
		trigger,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});
	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;
	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const handleDeletePhaseClick = (phaseId: string) => {
		setSelectedPhasesData(prevState => {
			return prevState.filter(phase => phaseId !== phase.id);
		});
	};

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);

			const quotaData: CreateQuotaData = {
				...data,
				invoiceDeliveryEndDate: formatDateToYYYYMMDD(data.invoiceDeliveryEndDate),
				invoiceDeliveryStartDate: formatDateToYYYYMMDD(data.invoiceDeliveryStartDate),
				paymentTargetDays: data.paymentTargetDays === 0 ? data.paymentTargetDaysCustom || 0 : data.paymentTargetDays,
				projectPhaseIds: selectedPhasesData.map(phase => phase.id),
			};
			await createQuota(clientId, quotaData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}

	}, [clientId, onSuccess, selectedPhasesData]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			{showPhaseSelect && <QuotaProjectPhaseSelector clientId={clientId}
														   onCancel={() => setShowPhaseSelect(false)}
														   selectedPhasesData={selectedPhasesData}
														   setSelectedPhasesData={setSelectedPhasesData} />}
			{!showPhaseSelect && <>
				<SidebarHeader>
					<SidebarHeaderHeadline>Neues Abrechnungskontingent</SidebarHeaderHeadline>
				</SidebarHeader>
				<SidebarContent>
					{isBusy && <SidebarBusyOverlay />}
					{!!serverErrorMessage &&
						<SidebarErrorOverlay title="Erstellung fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
					<FormSectionGroup>
						<FormSection title="Kontingentdetails">
							<div className="space-y-4">
								<Input name="title"
									   control={control}
									   label="Titel / Beschreibung" />
								<CurrencyInput name="budgetCents"
											   useControllerError={true}
											   control={control}
											   label="Budget" />
							</div>
						</FormSection>
						<FormSection title="Projektphasen">
							<div className="space-y-4">
								{selectedPhasesData.length > 0 && selectedPhasesData.map(phase => {
									return <SelectedQuotaProjectPhase key={phase.id}
																	  onDelete={handleDeletePhaseClick}
																	  phaseId={phase.id}
																	  phaseTitle={phase.phaseTitle}
																	  projectTitle={phase.projectTitle} />;
								})}
								<ButtonNewItem
									size="sm"
									theme="inline"
									onClick={() => setShowPhaseSelect(true)}
								>{selectedPhasesData.length > 0 && "Weitere "} Projektphase/n auswählen</ButtonNewItem>
								{selectedPhasesData.length === 0 && isSubmitted &&
									<FormInputError>Einem Abrechnungskontinent muss mindestens eine Phase zugewiesen
										werden.</FormInputError>}
							</div>
						</FormSection>
						<FormSection title="Rechnungsdetails">
							<div className="space-y-4">
								<Input name="orderNumber"
									   control={control}
									   label="Bestellnummer" />
								<div className="grid grid-cols-2 gap-x-4">
									<DatePickerInput name="invoiceDeliveryStartDate"
													 control={control}
													 clearable={false}
													 label="Leistungszeitraum Start" />
									<DatePickerInput name="invoiceDeliveryEndDate"
													 control={control}
													 clearable={false}
													 label="Leistungszeitraum Ende" />
								</div>
								<BillingInvoiceRecipientFormSection
									control={control}
									clientContactPersonSelectOptions={clientContactPersonSelectOptions}
									isSubmitted={isSubmitted}
									// @ts-expect-error - needs to be fixed
									errorMessage={errors[""]?.message}
									invoiceRecipientSelectOptions={clientsInvoiceRecipientSelectOptions}
									setValue={setValue}
									trigger={trigger}
									watch={watch}
								/>
							</div>
							<PaymentTargetDaysInput
								control={control}
								showCustomPaymentTargetInput={showCustomPaymentTargetInput}
							/>
						</FormSection>
					</FormSectionGroup>
				</SidebarContent>
				<SidebarFooter>
					<FormHasErrorsHint show={isSubmitted && (!isValid || selectedPhasesData.length === 0)}
									   className="mr-2" />
					<SubmitButton busy={isBusy}
								  disabled={!formIsSubmittable || (isSubmitted && selectedPhasesData.length === 0)}>
						Speichern
					</SubmitButton>
					<Button theme="white"
							onClick={onCancel}>
						Abbrechen
					</Button>
				</SidebarFooter>
			</>}

		</form>);
};

export default CreateQuotaForm;
